import React, { useState } from 'react';
import PartnerTransferActionBar from '../../PartnerTransferActionBar';
import { CalendarDay } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import FeeCard from './FeeCard';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import { useFormContext } from 'react-hook-form';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

interface FeeAllocation {
  commitmentId: string;
  investorName: string;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
  isTransferor: boolean;
}

interface Fee {
  feeKey: string;
  originalFeeId: string;
  lpFeeTemplateFeeName: string;
  accrualDate: CalendarDay;
  approvalDate?: CalendarDay;
  isApproved: boolean;
  originalAllocations: Array<FeeAllocation>;
  newAllocations: Array<FeeAllocation>;
}

interface TransferFeesContentProps {
  partnerTransferId: string;
  fees: Array<Fee>;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
  stepper: React.ReactNode;
}

const TransferFeesContent: React.FC<TransferFeesContentProps> = ({
  partnerTransferId,
  fees,
  baseCurrency,
  onNext,
  onPrevious,
  stepper,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { getValues } = useFormContext<PartnerTransfersFormValues>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const updatePartnerTransfer = api.partnerTransfers.update.useMutation(
    useHandleTrpcMutation()({
      onSuccess: () => {
        onNext();
      },
    }),
  );

  const rows = fees.map((fee) => ({
    ...fee,
    id: fee.originalFeeId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'name',
      label: 'Fee',
      render: ({ lpFeeTemplateFeeName, feeKey }) => (
        <ColorIndicatorWithText
          colorKey={{ feeKey, lpFeeTemplateFeeName }}
          text={lpFeeTemplateFeeName}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (fee) => (fee.isApproved ? 'Approved' : 'Draft'),
    },
    {
      key: 'accrualDate',
      label: 'Accrual date',
      render: (fee) => fee.accrualDate.toReadable(),
    },
  ];

  const [isNavigating, setIsNavigating] = useState(false);

  const handleNext = () => {
    setIsNavigating(true);
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId: selectedVehicleId ?? '',
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.Fees,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
      saveActionFees: true,
    };
    updatePartnerTransfer.mutate(input, {
      onError: () => setIsNavigating(false),
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={handlePrevious}
          onNext={handleNext}
          isNextLoading={isNavigating}
          isNextDisabled={isNavigating}
          isPreviousDisabled={isNavigating}
        />
      }
    >
      <Table
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <FeeCard fee={row} baseCurrency={baseCurrency} />
        )}
        columns={columns}
        rows={rows}
        emptyMessage="No approved fees found before the partner transfer date."
      />
    </Page>
  );
};

export default TransferFeesContent;
